/* eslint no-console: off */
/** The name of the module */
export const name = 'Logger';
export default class Logger {
    /**
       * Returns if logging is explicitly enabled beyond build process's environment setting
       *
       * @returns {boolean} If logging is currently explicitly enabled
       */
    static isEnabled() {
        /* prod-only */
        return false;
        /* end-prod-only */
        /* dev-only */
        return Logger.loggingEnabled;
        /* end-dev-only */
    }
    /**
       * Explicitly disable logging
       */
    static disable() {
        Logger.loggingEnabled = false;
    }
    /**
       * Explicitly enable logging
       */
    static enable() {
        Logger.loggingEnabled = true;
    }
    /**
       * A wrapper for console.log() that allows it to be removed in production builds
       *
       * @param message The message to log to the console
       */
    // explicitly turning off the any for the data because we want the ability to log anything
    static log(...message) {
        Logger.parser(window.console.log, ...message);
    }
    /**
       * A wrapper for console.debug() that allows it to be removed in production builds
       *
       * @param {String} message The message to log to the console
       */
    // explicitly turning off the any for the data because we want the ability to log anything
    static debug(...message) {
        Logger.parser(window.console.debug, ...message);
    }
    /**
       * A wrapper for console.warn() that allows it to be removed in production builds
       *
       * @param message The message to log to the console
       */
    // explicitly turning off the any for the data because we want the ability to log anything
    static warn(...message) {
        Logger.parser(window.console.warn, ...message);
    }
    /**
       * A wrapper for console.error() that allows it to be removed in production builds, but also offers the ability to
       * force the console.error() call in production if needed.
       *
       * @param {String} message The message to log to the console
       * @param {Boolean} shouldForce Should the error be written to the console, even in production?
       */
    static error(...params) {
        const [message, shouldForce] = params;
        if (shouldForce === true) {
            window.console.error(message);
        }
        else {
            /* dev-only */
            Logger.parser(window.console.error, message);
            /* end-dev-only */
        }
    }
    /**
       * A wrapper for console.trace() that allows it to be removed in production builds
       */
    static trace() {
        /* dev-only */
        window.console.trace();
        /* end-dev-only */
    }
    /**
       * A wrapper that allows a callback function with more complex logging to be called that will be removed in
       * production builds
       *
       * @param {Function} cb The callback function to call for logging
       */
    static callback(cb) {
        /* dev-only */
        cb();
        /* end-dev-only */
    }
    /**
       * A wrapper for console.group() that will call one or more functions between the group start and group end
       * commands. The functions can be a single anonymous function or one or more calls to additional Logger functions.
       *
       * @example
       * Logger.group('This is a group message', Logger.table.bind(null, var1), Logger.table.bind(null, var2));
       *
       * @param {string} message Message to display for the group
       * @param {Function[]} callbacks Functions to be executed between the group start and group end commands
       */
    static group(message = '', ...callbacks) {
        /* dev-only */
        window.console.group(message);
        callbacks.forEach((cb) => cb());
        window.console.groupEnd();
        /* end-dev-only */
    }
    /**
       * A wrapper for console.table() that allows it to be removed in production builds. It also provies a fallback for
       * browsers that don't support console.table.
       *
       * @param data An object literal to display in table format
       */
    static table(data) {
        /* dev-only */
        if (window.console.table) {
            window.console.table(data);
        }
        else {
            window.console.dir(data);
        }
        /* end-dev-only */
    }
    // explicitly turning off the any for the data because we want the ability to log anything
    static parser(logType, ...data) {
        /* dev-only */
        if (arguments.length > 1) {
            // If the argument is an array with more than 1 index
            logType(...data);
            return;
        }
        if (Array.isArray(data)) {
            logType(...data);
            return;
        }
        // Checks for if there's an object returned in message, prints array if condition returns true
        if (typeof data === 'object' && 'object' in data && data.object) {
            logType(data.object, data);
            return;
        }
        logType(data);
        /* end-dev-only */
    }
}
/** Is logging enabled (beyond build process's removal of logging in production ) */
Logger.loggingEnabled = true;
